/**
 * A link that leaves an app's own browser (Facebook's, Instagram's, TikTok's) for the phone's real
 * one, with the idea and the visitor's id in it (easy-signup handoff §4, 2026-10-04).
 *
 *  - iOS: `x-safari-https://…`, which Safari answers from inside other apps (iOS 17 and later).
 *  - Android: an `intent://` link with no package named, so the phone's default browser opens it.
 *
 * Neither is promised by the apps that host these browsers, which is why the screen also says how
 * to do it by hand. The id keeps the journey one line across the two browsers (analytics/funnel.ts
 * adopts `vid` when the new browser has none of its own).
 *
 * And the build, when one is running or waiting signed out (2026-10-24: an iPhone visitor inside
 * Facebook pressed "Open in Safari" while their app built, and nothing arrived there): its anonymous
 * id and key (`ba`, `bk`) and its name (`bn`), so Safari opens the building screen and takes it up as
 * an emailed link does (screens/new/Building.tsx). Private params: never sent to Meta, and gone from
 * the address bar on arrival (analytics/privateParams.ts).
 */
import { currentVisit } from '../analytics/visits';

export function openInBrowserUrl(
  { os, idea, build }: { os: string; idea?: string; build?: { anonId: string; requestKey: string; appName?: string } | null },
  origin = typeof window !== 'undefined' ? window.location.origin : 'https://build.saddle-sites.com',
): string | null {
  if (os !== 'ios' && os !== 'android') return null;
  const params = new URLSearchParams();
  if (idea?.trim()) params.set('idea', idea.trim());
  let vid = '';
  try {
    vid = localStorage.getItem('saddle.visitor') ?? '';
  } catch {
    vid = '';
  }
  if (vid) params.set('vid', vid);
  // The ad that brought them, so the other browser's visit is still tied to it.
  const visit = currentVisit();
  if (visit.ref === 'ad') params.set('utm_source', 'fb');
  if (visit.utm_campaign) params.set('utm_campaign', visit.utm_campaign);
  if (visit.utm_content) params.set('utm_content', visit.utm_content);
  params.set('ref_browser', 'in_app');
  const carriesBuild = !!(build?.anonId && build.requestKey);
  if (carriesBuild) {
    params.set('ba', build!.anonId);
    params.set('bk', build!.requestKey);
    if (build!.appName?.trim()) params.set('bn', build!.appName.trim().slice(0, 80));
  }
  const target = new URL(`${carriesBuild ? '/new/building' : '/new/idea'}?${params.toString()}`, origin);
  if (os === 'ios') return `x-safari-${target.toString()}`;
  return `intent://${target.host}${target.pathname}${target.search}#Intent;scheme=https;end`;
}
